import { AsyncValidatorFn, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { getMetadata, setMetadata } from '@app/core/models/portal-metadata';
import { StringMap } from '@app/utils/string-map';
import { FormMappingOption } from '../models/form-mapping-option';

function mergeIdentityFunction(o: any, value: any, attribute: string) {
  o[attribute] = value;
}

function parseIdentityFunction(value) {
  return value;
}

export type SelectorFunction<T> = (_: T) => any;

export type FormMappingAttributeDef = {
  asyncValidators?: AsyncValidatorFn[];
  defaultValue?: any;
  formGroupFunction?: () => UntypedFormGroup;
  initialValue?: any;
  isArray?: boolean;
  isGroup?: boolean;
  mergeFunction?: (model: any, value: any, attribute: string, form: StringMap<any>) => any;
  normaliseInput?: (value: any) => any;
  parseFunction?: (value: any, o: any) => any;
  selector?: string;
  updateOn?: 'blur' | 'change' | 'submit';
  validators?: ValidatorFn[];
};

/**
 * Decorator that collects all form mapping attributes for a FormMapping
 * @param selector Path to the property to use, can be nested (e.g. property1.property2.property3)
 * @param initialValue Initial value that is used for the formControl
 * @param validators List of validators for the formControl (see https://angular.io/api/forms/Validators for possible values)
 * @param mergeFunction Custom mapping function that will be used when merging the formMapping with the original object
 */
export function FormMappingAttribute<T>(
  selector?: string | FormMappingAttributeDef,
  initialValue?,
  validators?: ValidatorFn[],
  mergeFunction: (o: any, e: any, attr: string) => any = mergeIdentityFunction
): PortalDecorator {
  return function (instance, attribute: string) {
    const attrs: FormMappingOption[] = getMetadata(instance.constructor).formAttributes || [];

    if (selector === undefined) {
      selector = attribute;
    }

    if (typeof selector === 'string') {
      // TODO Remove this after refactoring all FormMapping models (deprecated)
      setMetadata(instance.constructor, {
        formAttributes: attrs.concat([
          {
            attribute,
            initialValue,
            selector,
            validators,
            mergeFunction,
            parseFunction: parseIdentityFunction,
          },
        ]),
      });
    } else {
      // Check if there already is a form attribute and if so extend it
      let formAttribute: FormMappingOption = attrs.find((a) => a.attribute === attribute);

      // New form mapping attribute
      const formMappingOption: FormMappingOption = {
        attribute,
        asyncValidators: selector.asyncValidators || [],
        defaultValue: selector.defaultValue,
        formGroupFunction: selector.formGroupFunction,
        initialValue: selector.initialValue,
        isArray: selector.isArray,
        isGroup: selector.isGroup,
        mergeFunction: selector.mergeFunction || mergeIdentityFunction,
        normaliseInput: selector.normaliseInput,
        parseFunction: selector.parseFunction || parseIdentityFunction,
        selector: selector.selector || attribute,
        updateOn: selector.updateOn,
        validators: selector.validators || [],
      };

      if (formAttribute !== undefined) {
        // Check if both form decorators define some properties
        if (checkForConflicts(formAttribute, formMappingOption)) {
          throw new Error('Conflicting decorator definitions!');
        }

        mergeFormAttributes(formAttribute, formMappingOption);
      } else {
        formAttribute = formMappingOption;

        setMetadata(instance.constructor, {
          formAttributes: attrs.concat([formAttribute]),
        });
      }
    }
  };
}

export type PortalDecorator = (instance: any, attribute: string) => void;

/**
 * Check if two form attribute definitions conflict with each other
 * @param o1 Form attribute definition
 * @param o2 Form attribute definition
 */
function checkForConflicts(o1: FormMappingOption, o2: FormMappingOption): boolean {
  return (
    (o1.defaultValue !== undefined && o2.defaultValue !== undefined) ||
    (o1.formGroupFunction !== undefined && o2.formGroupFunction !== undefined) ||
    (o1.isArray !== undefined && o2.isArray !== undefined) ||
    (o1.isGroup !== undefined && o2.isGroup !== undefined) ||
    (o1.initialValue !== undefined && o2.initialValue !== undefined) ||
    (o1.mergeFunction !== mergeIdentityFunction && o2.mergeFunction !== mergeIdentityFunction) ||
    (o1.normaliseInput !== undefined && o2.normaliseInput !== undefined) ||
    (o1.parseFunction !== parseIdentityFunction && o2.parseFunction !== parseIdentityFunction) ||
    (o1.selector !== o1.attribute && o2.selector !== o2.attribute && o1.selector !== o2.selector) ||
    (o1.updateOn !== undefined && o2.updateOn !== undefined)
  );
}

/**
 * Merge two form attributes into the first
 * @param o1 Existing form attribute
 * @param o2 New form attribute
 */
function mergeFormAttributes(o1: FormMappingOption, o2: FormMappingOption): void {
  o1.defaultValue = o1.defaultValue || o2.defaultValue;
  o1.isArray = o1.isArray || o2.isArray;
  o1.isGroup = o1.isGroup || o2.isGroup;
  o1.initialValue = o1.initialValue || o2.initialValue;
  o1.updateOn = o1.updateOn || o2.updateOn;

  if (o1.formGroupFunction === undefined) {
    o1.formGroupFunction = o2.formGroupFunction;
  }

  if (o1.mergeFunction === mergeIdentityFunction) {
    o1.mergeFunction = o2.mergeFunction;
  }

  if (o1.parseFunction === parseIdentityFunction) {
    o1.parseFunction = o2.parseFunction;
  }

  if (o1.normaliseInput === undefined) {
    o1.normaliseInput = o2.normaliseInput;
  }

  if (o1.selector === undefined) {
    o1.selector = o2.selector;
  }

  o1.asyncValidators = [].concat(o1.asyncValidators, o2.asyncValidators);
  o1.validators = [].concat(o1.validators, o2.validators);
}
